import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  User as UserIcon, Bell, QrCode, Languages, LogOut, LayoutDashboard,
  Store, LifeBuoy,
} from "lucide-react";
import { api } from "../lib/api";
import { useDashboardI18n } from "../lib/useDashboardI18n";
import SupportModal from "./SupportModal";

/**
 * Dashboard header used inside Business + Vendor dashboards on mobile.
 *
 * Layout mirrors the user-app header:
 *   [logo]            [bell] [user-icon ▼]
 *
 * The user-icon opens a dropdown that contains:
 *   • My Dashboard / My Profile  (link to overview tab)
 *   • Edit Profile                (link to profile tab)
 *   • QR Code                     (callback)
 *   • EN / ES language toggle     (instant i18n switch)
 *   • Support                     (opens SupportModal)
 *   • Log Out
 */
export default function DashboardHeader({
  kind,                 // "business" | "vendor"
  ownerName,
  ownerAvatarUrl,
  overviewPath,
  profilePath,
  onOpenQr,
  rightExtra,           // optional ReactNode (e.g. reactivation pill)
}) {
  const navigate = useNavigate();
  const { lang, setLang, t } = useDashboardI18n(kind);
  const [open, setOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const menuRef = useRef(null);

  useEffect(() => {
    api.get("/notifications", { params: { limit: 1 } }).then(({ data }) => {
      setUnread(data?.unread || 0);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const doLogOut = async () => {
    try { await api.post("/auth/logout"); } catch { /* no-op */ }
    window.location.href = "/";
  };

  const isBiz = kind === "business";
  const initials = (ownerName || (isBiz ? "B" : "V")).trim().slice(0, 2).toUpperCase();

  return (
    <>
      <header
        className="md:hidden fixed top-0 inset-x-0 z-40 px-4 py-3 flex items-center justify-between"
        style={{ background: "#0d0d0d", borderBottom: "0.5px solid #1a1a1a" }}
        data-testid={`${kind}-mobile-header`}
      >
        <Link to={overviewPath} className="flex items-center gap-2.5 min-w-0">
          {ownerAvatarUrl ? (
            <img src={ownerAvatarUrl} alt="" style={{ width: 40, height: 40, borderRadius: 10, background: "#1D1D1D", border: "1px solid rgba(232,184,75,0.3)", objectFit: "cover" }} />
          ) : (
            <div style={{
              width: 40, height: 40, borderRadius: 10, background: "#1D1D1D",
              border: "1px solid rgba(232,184,75,0.3)", display: "grid", placeItems: "center", color: "#E8B84B",
              fontFamily: "'Playfair Display', serif", fontSize: 14,
            }}>{initials}</div>
          )}
          <div className="min-w-0">
            <div className="truncate" style={{ fontFamily: "'Playfair Display', serif", fontSize: 15, color: "#F5F1E8", lineHeight: 1.1 }}>
              {ownerName}
            </div>
            <div className="truncate mt-0.5" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#9A9490" }}>
              {isBiz ? "Business" : "Vendor"}
            </div>
          </div>
        </Link>

        <div className="relative flex items-center gap-1" ref={menuRef}>
          <Link
            to="/notifications"
            data-testid={`${kind}-header-bell`}
            aria-label="Notifications"
            className="relative grid place-items-center"
            style={{ width: 40, height: 40, color: "#E8B84B" }}
          >
            <Bell className="w-5 h-5" strokeWidth={1.5} />
            {unread > 0 && (
              <span
                data-testid={`${kind}-header-bell-badge`}
                style={{
                  position: "absolute", top: 4, right: 4,
                  minWidth: 18, height: 18,
                  background: "#E8B84B", color: "#0B0B0B",
                  fontFamily: "'DM Sans', sans-serif", fontSize: 10, fontWeight: 700,
                  borderRadius: 999, padding: "0 4px",
                  display: "grid", placeItems: "center",
                }}
              >{unread > 9 ? "9+" : unread}</span>
            )}
          </Link>

          <button
            type="button"
            aria-label="Account menu"
            data-testid={`${kind}-header-user`}
            onClick={() => setOpen((o) => !o)}
            className="grid place-items-center rounded-full"
            style={{ width: 40, height: 40, color: "#E8B84B", border: "1px solid rgba(232,184,75,0.3)" }}
          >
            <UserIcon className="w-5 h-5" strokeWidth={1.5} />
          </button>

          {open && (
            <div
              data-testid={`${kind}-header-menu`}
              style={{
                position: "absolute", top: 48, right: 0,
                width: 240, background: "#171717",
                border: "1px solid rgba(232,184,75,0.3)", borderRadius: 12,
                boxShadow: "0 12px 32px rgba(0,0,0,0.6)",
                zIndex: 60,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              <MenuItem
                icon={LayoutDashboard}
                label={isBiz ? t("nav_my_dashboard") : t("nav_my_profile")}
                onClick={() => { setOpen(false); navigate(overviewPath); }}
                testid={`${kind}-menu-dashboard`}
              />
              <MenuItem
                icon={Store}
                label={t("nav_edit_profile")}
                onClick={() => { setOpen(false); navigate(profilePath); }}
                testid={`${kind}-menu-edit-profile`}
              />
              <MenuItem
                icon={QrCode}
                label={t("nav_qr")}
                onClick={() => { setOpen(false); onOpenQr?.(); }}
                testid={`${kind}-menu-qr`}
              />
              {/* EN / ES toggle */}
              <div
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 14px", color: "#F5F1E8", fontSize: 14,
                  borderBottom: "0.5px solid rgba(255,255,255,0.06)",
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <Languages className="w-4 h-4 text-gold" strokeWidth={1.5} /> {t("nav_language")}
                </span>
                <div style={{ display: "inline-flex", border: "1px solid rgba(232,184,75,0.4)", borderRadius: 999, padding: 2 }}>
                  {["en", "es"].map((code) => (
                    <button
                      key={code}
                      type="button"
                      data-testid={`${kind}-lang-${code}`}
                      onClick={() => setLang(code)}
                      style={{
                        padding: "4px 10px", minHeight: 28, borderRadius: 999, fontSize: 11,
                        background: lang === code ? "#E8B84B" : "transparent",
                        color: lang === code ? "#0B0B0B" : "#E8B84B",
                        textTransform: "uppercase", letterSpacing: 1,
                      }}
                    >
                      {code}
                    </button>
                  ))}
                </div>
              </div>
              <MenuItem
                icon={LifeBuoy}
                label={t("nav_support")}
                onClick={() => { setOpen(false); setSupportOpen(true); }}
                testid={`${kind}-menu-support`}
              />
              <MenuItem
                icon={LogOut}
                label={t("nav_signout")}
                onClick={() => { setOpen(false); doLogOut(); }}
                testid={`${kind}-menu-signout`}
                last
              />
            </div>
          )}
        </div>
      </header>

      {rightExtra}

      <SupportModal open={supportOpen} onClose={() => setSupportOpen(false)} />
    </>
  );
}

function MenuItem({ icon: Icon, label, onClick, testid, last }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 10,
        padding: "12px 14px", color: "#F5F1E8", fontSize: 14, textAlign: "left",
        borderBottom: last ? "none" : "0.5px solid rgba(255,255,255,0.06)",
        minHeight: 48,
      }}
    >
      <Icon className="w-4 h-4 text-gold" strokeWidth={1.5} />
      <span>{label}</span>
    </button>
  );
}
